import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  AddCircleOutlineOutlined,
  Autorenew,
  EditOutlined,
} from "@mui/icons-material";
import {
  Checkbox,
  DialogContent,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { Language } from "@safetyculture/s12-apis-connect-web/s12/common/languages_pb";
import {
  BillingDetails,
  Contact,
  Address as SubscriptionAddress,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_pb";

import {
  createBillingDetails,
  updateBillingDetails,
} from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import PillStatusTrigger from "components/org-display/details-display-cards/pill-status-trigger";
import AddressForm from "components/shared/address/address-form";
import ConfirmDialog from "components/shared/confirm-dialog";
import { PillStatusMode, languageDisplayMapping } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { useAddress } from "helpers/hooks/use-address";
import {
  useOrgName,
  useOrgRoleInfo,
  useOrgRoleInfoAddress,
} from "helpers/hooks/use-org-details";
import { toUUID } from "helpers/s12-helpers";
import { Address } from "types/address";
import { UrlParamTypes } from "types/org-display";

type ContactFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

type BillingDetailsStatusCheckProps = {
  billingDetails: BillingDetails | undefined;
  statusMode: PillStatusMode;
  refreshBillingDetails: () => void;
  /** For a caller that already labels the field - the dashboard's cards. */
  iconOnly?: boolean;
};

const BillingDetailsForm = ({
  billingDetails,
  statusMode,
  refreshBillingDetails,
  iconOnly,
}: BillingDetailsStatusCheckProps) => {
  const { showError } = useContext(SnackbarContext);
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const [inProgress, setInProgress] = useState(false);
  const [isSameShipping, setIsSameShipping] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  // Use ref to track if we're in initial load to avoid overwriting shipping address
  const isInitialLoadRef = useRef(true);
  const orgDetailsRoleName = useOrgName();
  const { abn, email, phone, language } = useOrgRoleInfo();
  const { city, countryCode, postalCode, region, street, streetCont } =
    useOrgRoleInfoAddress();

  const setDefaultBillingDetails = useCallback(() => {
    switch (statusMode) {
      // If there is no existing billing details, populate default values based on role.info.
      case PillStatusMode.CREATE: {
        return {
          legalName: orgDetailsRoleName,
          taxNumber: abn,
          // languageDisplayMapping uses "N/A" for UNSPECIFIED, which fails the
          // api-subscriptions Locale regex. Default to en-US in that case.
          locale:
            language === Language.UNSPECIFIED
              ? "en-US"
              : languageDisplayMapping[language],
        };
      }
      default: {
        return {
          legalName: billingDetails?.legalName || "",
          taxNumber: billingDetails?.taxNumber || "",
          locale: billingDetails?.locale || "",
        };
      }
    }
  }, [statusMode, orgDetailsRoleName, abn, language, billingDetails]);

  const setDefaultBillingContact = useCallback((): ContactFormState => {
    switch (statusMode) {
      case PillStatusMode.CREATE: {
        // If there is no existing billing details, populate default values based on role.info.
        return {
          firstName: "",
          lastName: "",
          email: email,
          phoneNumber: phone,
        };
      }
      default: {
        // Check for new fields first, fall back to deprecated fields for backwards compatibility
        if (billingDetails?.billingContact) {
          return {
            firstName: billingDetails.billingContact.firstName || "",
            lastName: billingDetails.billingContact.lastName || "",
            // Fall back to deprecated contactEmail if billingContact.email is empty
            email:
              billingDetails.billingContact.email ||
              billingDetails?.contactEmail ||
              "",
            // Fall back to deprecated contactNumber if billingContact.phoneNumber is empty
            phoneNumber:
              billingDetails.billingContact.phoneNumber ||
              billingDetails?.contactNumber ||
              "",
          };
        } else {
          // Backwards compatibility: fall back to deprecated fields
          return {
            firstName: "",
            lastName: "",
            email: billingDetails?.contactEmail || "",
            phoneNumber: billingDetails?.contactNumber || "",
          };
        }
      }
    }
  }, [statusMode, email, phone, billingDetails]);

  const setDefaultShippingContact = useCallback((): ContactFormState => {
    switch (statusMode) {
      case PillStatusMode.CREATE: {
        // If there is no existing shipping contact, populate default values based on role.info.
        return {
          firstName: "",
          lastName: "",
          email: email,
          phoneNumber: phone,
        };
      }
      default: {
        // Check for shipping contact, fall back to billing contact or deprecated fields
        if (billingDetails?.shippingContact) {
          return {
            firstName: billingDetails.shippingContact.firstName || "",
            lastName: billingDetails.shippingContact.lastName || "",
            // Fall back to billingContact or deprecated contactEmail
            email:
              billingDetails.shippingContact.email ||
              billingDetails?.billingContact?.email ||
              billingDetails?.contactEmail ||
              "",
            // Fall back to billingContact or deprecated contactNumber
            phoneNumber:
              billingDetails.shippingContact.phoneNumber ||
              billingDetails?.billingContact?.phoneNumber ||
              billingDetails?.contactNumber ||
              "",
          };
        } else if (billingDetails?.billingContact) {
          // If no shipping contact, default to billing contact (with deprecated fallback)
          return {
            firstName: billingDetails.billingContact.firstName || "",
            lastName: billingDetails.billingContact.lastName || "",
            email:
              billingDetails.billingContact.email ||
              billingDetails?.contactEmail ||
              "",
            phoneNumber:
              billingDetails.billingContact.phoneNumber ||
              billingDetails?.contactNumber ||
              "",
          };
        } else {
          // Backwards compatibility: fall back to deprecated fields
          return {
            firstName: "",
            lastName: "",
            email: billingDetails?.contactEmail || "",
            phoneNumber: billingDetails?.contactNumber || "",
          };
        }
      }
    }
  }, [statusMode, email, phone, billingDetails]);

  const setDefaultBillingAddress = useCallback(() => {
    switch (statusMode) {
      case PillStatusMode.CREATE: {
        // If there is no existing billing details, populate default values based on role.info.address.
        return {
          address: street,
          postcode: postalCode,
          city: city,
          state: region,
          country: countryCode,
          address2: streetCont,
        };
      }
      default: {
        return {
          address: billingDetails?.billingAddress?.thoroughfare || "",
          postcode: billingDetails?.billingAddress?.postalCode || "",
          city: billingDetails?.billingAddress?.locality || "",
          state: billingDetails?.billingAddress?.administrativeArea || "",
          country: billingDetails?.billingAddress?.country || "",
          address2: billingDetails?.billingAddress?.subThoroughfare || "",
        };
      }
    }
  }, [
    statusMode,
    street,
    postalCode,
    city,
    region,
    countryCode,
    streetCont,
    billingDetails,
  ]);

  const setDefaultShippingAddress = useCallback(() => {
    switch (statusMode) {
      case PillStatusMode.CREATE: {
        // If there is no existing shipping details, populate default values based on role.info.address.
        return {
          address: street,
          postcode: postalCode,
          city: city,
          state: region,
          country: countryCode,
          address2: streetCont,
        };
      }
      default: {
        return {
          address: billingDetails?.shippingAddress?.thoroughfare || "",
          postcode: billingDetails?.shippingAddress?.postalCode || "",
          city: billingDetails?.shippingAddress?.locality || "",
          state: billingDetails?.shippingAddress?.administrativeArea || "",
          country: billingDetails?.shippingAddress?.country || "",
          address2: billingDetails?.shippingAddress?.subThoroughfare || "",
        };
      }
    }
  }, [
    statusMode,
    street,
    postalCode,
    city,
    region,
    countryCode,
    streetCont,
    billingDetails,
  ]);

  const [formState, setFormState] = useState(setDefaultBillingDetails);
  const [billingContact, setBillingContact] = useState<ContactFormState>(
    setDefaultBillingContact(),
  );
  const [shippingContact, setShippingContact] = useState<ContactFormState>(
    setDefaultShippingContact(),
  );
  const [isSameShippingContact, setIsSameShippingContact] = useState(true);
  const [billingAddress, setBillingAddress] = useAddress({
    ...setDefaultBillingAddress(),
  });
  const [shippingAddress, setShippingAddress] = useAddress({
    ...setDefaultShippingAddress(),
  });

  const isSubmitDisabled =
    !billingContact.firstName ||
    !billingContact.lastName ||
    !billingContact.email ||
    !billingAddress.country ||
    (billingAddress.country === "US" && !billingAddress.postcode);

  // Helper function to check if two addresses are the same
  const areAddressesSame = useCallback((addr1: Address, addr2: Address) => {
    return (
      addr1.address === addr2.address &&
      addr1.postcode === addr2.postcode &&
      addr1.city === addr2.city &&
      addr1.state === addr2.state &&
      addr1.country === addr2.country &&
      addr1.address2 === addr2.address2
    );
  }, []);

  // Helper function to check if two contacts are the same
  const areContactsSame = useCallback(
    (contact1: ContactFormState, contact2: ContactFormState) => {
      return (
        contact1.firstName === contact2.firstName &&
        contact1.lastName === contact2.lastName &&
        contact1.email === contact2.email &&
        contact1.phoneNumber === contact2.phoneNumber
      );
    },
    [],
  );

  // Update state when billingDetails prop changes
  useEffect(() => {
    isInitialLoadRef.current = true; // Mark as initial load

    setFormState(setDefaultBillingDetails());
    const newBillingContact = setDefaultBillingContact();
    const newShippingContact = setDefaultShippingContact();
    const newBillingAddress = { ...setDefaultBillingAddress() };
    const newShippingAddress = { ...setDefaultShippingAddress() };

    setBillingContact(newBillingContact);
    setShippingContact(newShippingContact);
    setBillingAddress(newBillingAddress);
    setShippingAddress(newShippingAddress);

    // Set isSameShipping based on whether addresses are actually the same
    const addressesAreSame = areAddressesSame(
      newBillingAddress,
      newShippingAddress,
    );
    setIsSameShipping(addressesAreSame);

    // Set isSameShippingContact based on whether contacts are actually the same
    const contactsAreSame = areContactsSame(
      newBillingContact,
      newShippingContact,
    );
    setIsSameShippingContact(contactsAreSame);

    // Mark initial load as complete after state updates
    setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingDetails, statusMode]); // Only depend on the actual data, not the functions

  // Update shipping address when billing address changes and isSameShipping is true
  // But only after initial load to avoid overriding the initial shipping address
  useEffect(() => {
    if (isSameShipping && !isInitialLoadRef.current) {
      setShippingAddress({
        address: billingAddress.address || "",
        postcode: billingAddress.postcode || "",
        city: billingAddress.city || "",
        state: billingAddress.state || "",
        country: billingAddress.country || "",
        address2: billingAddress.address2 || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAddress, isSameShipping]); // Update shipping when billing changes

  // Update shipping contact when billing contact changes and isSameShippingContact is true
  // But only after initial load to avoid overriding the initial shipping contact
  useEffect(() => {
    if (isSameShippingContact && !isInitialLoadRef.current) {
      setShippingContact({
        firstName: billingContact.firstName || "",
        lastName: billingContact.lastName || "",
        email: billingContact.email || "",
        phoneNumber: billingContact.phoneNumber || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingContact, isSameShippingContact]); // Update shipping contact when billing contact changes

  const onbillingAddressChange = (updatedbillingAddress: Address) => {
    setBillingAddress(updatedbillingAddress);
  };

  const resetState = () => {
    isInitialLoadRef.current = true; // Mark as initial load

    setFormState(setDefaultBillingDetails());
    const newBillingContact = setDefaultBillingContact();
    const newShippingContact = setDefaultShippingContact();
    const newBillingAddress = { ...setDefaultBillingAddress() };
    const newShippingAddress = { ...setDefaultShippingAddress() };

    setBillingContact(newBillingContact);
    setShippingContact(newShippingContact);
    setBillingAddress(newBillingAddress);
    setShippingAddress(newShippingAddress);

    // Set isSameShipping based on whether addresses are actually the same
    const addressesAreSame = areAddressesSame(
      newBillingAddress,
      newShippingAddress,
    );
    setIsSameShipping(addressesAreSame);

    // Set isSameShippingContact based on whether contacts are actually the same
    const contactsAreSame = areContactsSame(
      newBillingContact,
      newShippingContact,
    );
    setIsSameShippingContact(contactsAreSame);

    // Mark initial load as complete after state updates
    setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 0);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
  ) => {
    setFormState({
      ...formState,
      [key]: event.target.value,
    });
  };

  const handleBillingContactChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: keyof ContactFormState,
  ) => {
    setBillingContact({
      ...billingContact,
      [key]: event.target.value,
    });
  };

  const handleShippingContactChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: keyof ContactFormState,
  ) => {
    setShippingContact({
      ...shippingContact,
      [key]: event.target.value,
    });
  };

  const onSameShippingContactChange = (value: boolean) => {
    setIsSameShippingContact(value);

    if (value) {
      // If checking the box, copy billing contact to shipping
      setShippingContact({
        firstName: billingContact.firstName || "",
        lastName: billingContact.lastName || "",
        email: billingContact.email || "",
        phoneNumber: billingContact.phoneNumber || "",
      });
    }
    // If unchecking, leave shipping contact as is so user can edit independently
  };

  const onSameShippingChange = (value: boolean) => {
    setIsSameShipping(value);

    if (value) {
      // If checking the box, copy billing address to shipping
      setShippingAddress({
        address: billingAddress.address || "",
        postcode: billingAddress.postcode || "",
        city: billingAddress.city || "",
        state: billingAddress.state || "",
        country: billingAddress.country || "",
        address2: billingAddress.address2 || "",
      });
    }
    // If unchecking, leave shipping address as is so user can edit independently
  };

  const onShippingAddressStateChange = (
    updatedShippingAddressState: Address,
  ) => {
    setShippingAddress(updatedShippingAddressState);
  };

  const submitForm = async () => {
    setInProgress(true);
    const bd = new BillingDetails({
      organisationId: toUUID(roleId),
      legalName: formState.legalName,
      taxNumber: formState.taxNumber,
      locale: formState.locale,
    });

    const billingContactObj = new Contact({
      firstName: billingContact.firstName,
      lastName: billingContact.lastName,
      email: billingContact.email,
      phoneNumber: billingContact.phoneNumber,
    });

    const shippingContactObj = new Contact({
      firstName: shippingContact.firstName,
      lastName: shippingContact.lastName,
      email: shippingContact.email,
      phoneNumber: shippingContact.phoneNumber,
    });

    const billingAddr = new SubscriptionAddress({
      thoroughfare: billingAddress.address,
      subThoroughfare: billingAddress.address2 || "",
      locality: billingAddress.city,
      postalCode: billingAddress.postcode,
      administrativeArea: billingAddress.state,
      country: billingAddress.country,
    });

    const shippingAddr = new SubscriptionAddress({
      thoroughfare: shippingAddress.address,
      subThoroughfare: shippingAddress.address2 || "",
      locality: shippingAddress.city,
      postalCode: shippingAddress.postcode,
      administrativeArea: shippingAddress.state,
      country: shippingAddress.country,
    });

    bd.billingContact = billingContactObj;
    bd.shippingContact = shippingContactObj;
    bd.billingAddress = billingAddr;
    bd.shippingAddress = shippingAddr;

    if (statusMode === PillStatusMode.CREATE) {
      createBillingDetails(
        {
          billingDetails: bd,
        },
        callOptionsHeaders(regionCode, roleId),
      )
        .catch((error) => {
          showError(error);
        })
        .finally(() => {
          refreshBillingDetails();
          setInProgress(false);
        });
    } else {
      updateBillingDetails(
        {
          orgId: toUUID(roleId),
          billingDetails: bd,
        },
        callOptionsHeaders(regionCode, roleId),
      )
        .catch((error) => {
          showError(error);
        })
        .finally(() => {
          refreshBillingDetails();
          setInProgress(false);
        });
    }
  };

  const handleOnConfirm = () => {
    if ([PillStatusMode.CREATE, PillStatusMode.EDIT].includes(statusMode)) {
      submitForm();
    }
  };

  const shouldSpin = inProgress ? "spin-1s" : "";
  const billingDetailsStatus = () => {
    switch (statusMode) {
      case PillStatusMode.EDIT: {
        return (
          <PillStatusTrigger
            label="Edit billing details"
            testId="pill-status-edit-billing-details"
            disabled={inProgress}
            iconOnly={iconOnly}
            textColor="#178a02"
            onClick={() => setOpenConfirmDialog(true)}
            icon={<EditOutlined style={{ color: "#178a02" }} />}
          />
        );
      }
      case PillStatusMode.CREATE: {
        return (
          <PillStatusTrigger
            label="Create billing details"
            testId="pill-status-create-billing-details"
            color="warning"
            disabled={inProgress}
            iconOnly={iconOnly}
            onClick={() => setOpenConfirmDialog(true)}
            icon={
              inProgress ? (
                <Autorenew className={shouldSpin} />
              ) : (
                <AddCircleOutlineOutlined fontSize="small" />
              )
            }
          />
        );
      }
      default: {
        return (
          <PillStatusTrigger
            label="Check billing details"
            testId="pill-status-check-billing-details"
            disabled={inProgress}
            iconOnly={iconOnly}
            onClick={refreshBillingDetails}
            icon={<Autorenew className={shouldSpin} />}
          />
        );
      }
    }
  };

  const billingAddressDisplay = () => {
    const displayText: string[] = [];

    Object.keys(billingAddress).map((k) => {
      const value = billingAddress[k as keyof Address] || "";
      if (value !== "") {
        displayText.push(value);
      }
    });

    return displayText.join(", ");
  };

  const billingDetailsDialogue = () => {
    return (
      <DialogContent data-testid="billing-details-form">
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              required
              name="Legal entity name"
              label="Legal entity name"
              helperText="This company's official registered name that appears on invoices. This may be different to the name of the SafetyCulture organization."
              inputProps={{
                "data-testid": "billing-details-legal-entity-name-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => handleChange(e, "legalName")}
              value={formState.legalName}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="Tax ID number"
              label="Tax ID number"
              inputProps={{
                "data-testid": "billing-details-tax-id-number-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => handleChange(e, "taxNumber")}
              value={formState.taxNumber}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1">Billing Contact</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="firstName"
              label="First name"
              inputProps={{
                "data-testid": "billing-contact-first-name-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={billingContact.firstName}
              onChange={(e) => handleBillingContactChange(e, "firstName")}
              error={!billingContact.firstName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="lastName"
              label="Last name"
              inputProps={{
                "data-testid": "billing-contact-last-name-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={billingContact.lastName}
              onChange={(e) => handleBillingContactChange(e, "lastName")}
              error={!billingContact.lastName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="email"
              label="Email address"
              inputProps={{
                "data-testid": "billing-contact-email-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={billingContact.email}
              onChange={(e) => handleBillingContactChange(e, "email")}
              error={!billingContact.email}
            />
          </Grid>
          <Grid item xs={6}>
            {/* TODO: https://safetyculture.atlassian.net/browse/BSA-2525
            Replace generic text field with a phone input component.*/}
            <TextField
              name="phone"
              label="Phone number"
              inputProps={{
                "data-testid": "billing-contact-phone-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              value={billingContact.phoneNumber}
              onChange={(e) => handleBillingContactChange(e, "phoneNumber")}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1">Billing Address</Typography>
          </Grid>
          <AddressForm
            data-testid="billing-details-billing-addr"
            address={billingAddress}
            onAddressChange={onbillingAddressChange}
          />
          <Grid item xs={12}>
            <Typography variant="body1">Shipping Contact</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSameShippingContact}
                  onChange={(_event, v) => {
                    onSameShippingContactChange(v);
                  }}
                  data-testid="billing-details-same-shipping-contact-checkbox"
                />
              }
              label="Same as billing contact"
              labelPlacement="end"
            />
          </Grid>
          {!isSameShippingContact ? (
            <>
              <Grid item xs={6}>
                <TextField
                  name="firstName"
                  label="First name"
                  inputProps={{
                    "data-testid": "shipping-contact-first-name-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  value={shippingContact.firstName}
                  onChange={(e) => handleShippingContactChange(e, "firstName")}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="lastName"
                  label="Last name"
                  inputProps={{
                    "data-testid": "shipping-contact-last-name-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  value={shippingContact.lastName}
                  onChange={(e) => handleShippingContactChange(e, "lastName")}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="email"
                  label="Email address"
                  inputProps={{
                    "data-testid": "shipping-contact-email-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  value={shippingContact.email}
                  onChange={(e) => handleShippingContactChange(e, "email")}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="phone"
                  label="Phone number"
                  inputProps={{
                    "data-testid": "shipping-contact-phone-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  value={shippingContact.phoneNumber}
                  onChange={(e) =>
                    handleShippingContactChange(e, "phoneNumber")
                  }
                />
              </Grid>
            </>
          ) : null}
          <Grid item xs={12}>
            <Typography variant="body1">Shipping Address</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSameShipping}
                  onChange={(_event, v) => {
                    onSameShippingChange(v);
                  }}
                  data-testid="billing-details-same-shipping-checkbox"
                />
              }
              label={`Same as billing address(${billingAddressDisplay()})`}
              labelPlacement="end"
            />
          </Grid>
          {!isSameShipping ? (
            <AddressForm
              data-testid="billing-details-shipping-addr"
              address={shippingAddress}
              onAddressChange={onShippingAddressStateChange}
            />
          ) : null}
        </Grid>
      </DialogContent>
    );
  };

  return (
    <>
      {billingDetailsStatus()}
      <ConfirmDialog
        title="Billing details"
        width="md"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={handleOnConfirm}
        onCancel={resetState}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="OK"
        disabled={isSubmitDisabled}
      >
        {billingDetailsDialogue()}
      </ConfirmDialog>
    </>
  );
};

export default BillingDetailsForm;
